import { CheckCircleIcon, TimeIcon } from '@chakra-ui/icons';
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Flex,
	Heading,
	List,
	ListIcon,
	ListItem,
	Stack,
	Text,
	useBreakpointValue,
	useColorModeValue,
} from '@chakra-ui/react';
import { AttachMoney } from '@mui/icons-material';
import { rewardService } from 'api/services/rewardService';
import { LoadingSpinner } from 'components/loading/LoadingSpinner';
import ResultMessage from 'components/modal/ResultMessage';
import { ALERT, SUCCESS } from 'constant/messages';
import { NAVIGATE } from 'constant/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { storeActions } from 'store/store';
import { userActions } from 'store/user';
import { pulse } from 'utils/animation';
import { handleCloseModal, handleMessage } from 'utils/hooks';
import {
	fetchConversionRates,
	formatBalance,
	handleCurrencyChange,
} from 'utils/rates';

export default function Rewards() {
	const TOKEN = useSelector((state) => state.auth.tokenId);
	const isMobile = useBreakpointValue({ base: true, md: false });
	const {
		userLightningAddress,
		userReferrals,
		userBalanceAvailable,
		userBalanceTotal,
	} = useSelector((state) => state.user);
	const { storeAllReward, storeAllLink, storeAllConversion } = useSelector(
		(state) => state.store
	);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [rewardData, setRewardData] = useState(storeAllReward);
	const [loading, setLoading] = useState(false);
	const [claimLoading, setClaimLoading] = useState(false);
	const [message, setMessage] = useState({
		state: false,
		title: '',
		subtitle: '',
	});

	useEffect(() => {
		fetchConversionRates(setMxnValue, setUsdValue);
	}, []);

	const handleClaim = async (stepKey) => {
		if (!stepKey) return;
		try {
			setClaimLoading(stepKey);
			const result = await rewardService.claim(TOKEN, stepKey);
			if (result.status === 200 && result.data.status === 'SUCCESS') {
				const updatedRewards = rewardData.map((step) =>
					step.stepKey === stepKey ? { ...step, completed: true } : step
				);
				const claimedStep = rewardData.find((step) => step.stepKey === stepKey);
				setRewardData(updatedRewards);

				dispatch(storeActions.setStoreData({ storeAllReward: updatedRewards }));
				dispatch(
					userActions.setUserBalance({
						userBalanceAvailable:
							userBalanceAvailable + (claimedStep?.sats || 0),
						userBalanceTotal: userBalanceTotal + (claimedStep?.sats || 0),
					})
				);
				handleMessage(
					'success',
					ALERT.SUCCESS_TITLE,
					SUCCESS.REWARD_SUCCESS,
					setMessage
				);
			} else {
				throw new Error('Claim unsuccessful');
			}
		} catch (error) {
			handleMessage(
				'error',
				ALERT.ERROR_TITLE,
				ALERT.ERROR_SUBTITLE,
				setMessage
			);
			console.error(error);
		} finally {
			setClaimLoading(false);
		}
	};

	const [mxnValue, setMxnValue] = useState(0);
	const [usdValue, setUsdValue] = useState(0);
	const [currentCurrency, setCurrentCurrency] = useState('SATS');

	const completedCount = rewardData.filter((s) => s.completed).length;
	const cardBg = useColorModeValue('gray.100', 'gray.500');
	const { validSteps, callToAction } = useMemo(() => {
		const validityMap = {};
		let firstUnmetAction = null;

		for (const step of rewardData) {
			switch (step.stepKey) {
				case 'add_wallet':
					validityMap[step.stepKey] = !!userLightningAddress;
					if (!userLightningAddress && !firstUnmetAction) {
						firstUnmetAction = {
							link: NAVIGATE.PROFILE_OVERVIEW,
							label: '¡Gana más completando tu perfil!',
						};
					}
					break;
				case 'click_store':
					validityMap[step.stepKey] = storeAllLink?.length > 0;
					if (!(storeAllLink?.length > 0) && !firstUnmetAction) {
						firstUnmetAction = {
							link: NAVIGATE.BRANDS,
							label: '¡Gana más explorando nuestras tiendas!',
						};
					}
					break;
				case 'first_purchase':
					validityMap[step.stepKey] = storeAllConversion?.length > 0;
					if (!(storeAllConversion?.length > 0) && !firstUnmetAction) {
						firstUnmetAction = {
							link: NAVIGATE.BRANDS,
							label: '¡Gana más realizando una compra!',
						};
					}
					break;
				case 'refer_link':
					validityMap[step.stepKey] = userReferrals > 0;
					if (!(userReferrals > 0) && !firstUnmetAction) {
						firstUnmetAction = {
							link: NAVIGATE.CLIENT_DASHBOARD,
							label: '¡Gana refiriendo!',
						};
					}
					break;
				default:
					validityMap[step.stepKey] = false;
			}
		}

		// Optional fallback if everything is already completed
		if (!firstUnmetAction) {
			firstUnmetAction = {
				link: '/client/home',
				label: '¡Felicidades! Has completado todos los pasos.',
			};
		}

		return { validSteps: validityMap, callToAction: firstUnmetAction };
	}, [
		userLightningAddress,
		storeAllLink,
		storeAllConversion,
		userReferrals,
		rewardData,
	]);

	return (
		<Flex direction='column' pt={{ sm: '125px', lg: '75px' }}>
			<Heading mb='20px' fontSize='2xl' ms='24px' fontWeight='700'>
				Tu progreso con Bumbei
			</Heading>
			<Card bg={cardBg} boxShadow='md' mb='20px' p={{ base: 2, md: 8 }}>
				<LoadingSpinner loading={loading} />
				{!loading && rewardData.length > 0 && (
					<Stack spacing={6}>
						<Text>
							Completa los pasos y gana recompensas en bitcoin por cada acción
							realizada.
						</Text>
						<Text fontWeight='medium'>
							{completedCount} de {rewardData.length} pasos completados
						</Text>

						<List spacing={3} pt={4}>
							{rewardData.length > 0 &&
								rewardData.map((step) => (
									<ListItem
										key={step.stepKey}
										display='flex'
										justifyContent='space-between'
										alignItems='center'
										bg={step.completed ? 'green.500' : cardBg}
										px={4}
										py={3}
										borderRadius='lg'
										boxShadow='sm'
										flexWrap='wrap'
										gap={5}
									>
										<Flex
											alignItems='center'
											minW={isMobile && '100%'}
											w={isMobile && '100%'}
										>
											<ListIcon
												as={step.completed ? CheckCircleIcon : TimeIcon}
												color={step.completed ? 'gray.100' : 'gray.400'}
											/>
											<Text
												fontWeight='medium'
												color={step.completed && 'gray.100'}
												fontSize={{ base: 'sm', md: 'md' }}
											>
												{step.label}
											</Text>
											{!step.completed && validSteps[step.stepKey] && (
												<>
													<Button
														mx={'20px'}
														px={{ base: '15px', md: '10px' }}
														size='sm'
														variant='outline'
														onClick={() => handleClaim(step.stepKey)}
														animation={`${pulse} 1s infinite`}
														minW='fit-content'
													>
														{!isMobile ? (
															'Reclamar'
														) : (
															<AttachMoney fontSize='md' />
														)}
													</Button>
													{claimLoading === step.stepKey && (
														<CircularProgress
															isIndeterminate
															color='brand.500'
															size='25px'
														/>
													)}
												</>
											)}
										</Flex>
										<Button
											onClick={() => handleCurrencyChange(setCurrentCurrency)}
										>
											<Text
												fontSize='sm'
												fontWeight='bold'
												textAlign='right'
												variant='ghost'
											>
												{formatBalance(
													step.sats,
													currentCurrency,
													usdValue,
													mxnValue
												)}
											</Text>
										</Button>
									</ListItem>
								))}
						</List>

						<Box pt={6}>
							<Button
								onClick={() => navigate(callToAction.link)}
								variant='brand'
							>
								{callToAction.label}
							</Button>
						</Box>
					</Stack>
				)}
			</Card>
			<ResultMessage
				isOpen={message.state}
				onClose={() => handleCloseModal(setMessage)}
				message={message}
			/>
		</Flex>
	);
}
